<template>
  <div class="account-editor">
    <div class="mt-1">
      <div class="label-with-tooltip mb-1">
        <span class="field-label">{{ fieldLabel }}:</span>
        <Tooltip v-if="tooltipText" width="300" placement="top">
          <template #trigger>
            <i class="ml-1 fas fa-question-circle"></i>
          </template>
          <template #tooltip>
            <div class="text-center">
              {{ tooltipText }}
            </div>
          </template>
        </Tooltip>
      </div>
      <div @focusout="handleBlur" @mousedown="preventButtonBlur">
        <InputField
          :id="`account-${fieldName}`"
          v-model="accountName"
          action-text="Save"
          cancellable
          :locked="!editing"
          :validated="!editing"
          :loading="saving"
          :helper-text="formError"
          :validation="formError ? 'error' : 'none'"
          @action="handleSave"
          @unlock="editAccount"
          @cancel="cancelEdit"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";

import InputField from "@/components/Seranova/InputField/InputField.vue";
import Tooltip from "@/components/Tooltip.vue";
import { injectUseQuickbooksConfig } from "./useQuickbooksConfig";

const props = defineProps({
  companyId: {
    type: Number,
    default: -1,
    required: true
  },
  fieldName: {
    type: String,
    default: "",
    required: true
  },
  fieldLabel: {
    type: String,
    default: "",
    required: true
  },
  tooltipText: {
    type: String,
    default: "",
    required: false
  },
  accountName: {
    type: String,
    default: "",
    required: false
  },
  fieldRequired: {
    type: Boolean,
    default: true,
    required: false
  }
});

function missingError() {
  return props.fieldRequired && !props.accountName ? "Required" : "";
}

const editing = ref(false);
const saving = ref(false);
const accountName = ref(props.accountName || "");
const formError = ref(missingError());
const { saveAccount } = injectUseQuickbooksConfig();

watch(
  () => [props.accountName, props.fieldRequired],
  () => {
    if (editing.value) return;
    accountName.value = props.accountName || "";
    formError.value = missingError();
  }
);

function editAccount() {
  editing.value = true;
}

function cancelEdit() {
  formError.value = missingError();
  accountName.value = props.accountName || "";
  editing.value = false;
}

function preventButtonBlur(event: MouseEvent) {
  // Chrome doesn't focus buttons on click, so clicking Save/Cancel/Edit
  // causes focusout with relatedTarget=null — the blur handler then
  // mistakenly cancels editing. Preventing default on mousedown keeps
  // focus on the input so focusout never fires for button clicks.
  if ((event.target as HTMLElement).closest("button")) {
    event.preventDefault();
  }
}

function handleBlur(event: FocusEvent) {
  const container = event.currentTarget as HTMLElement;
  if (container.contains(event.relatedTarget as Node)) return;
  if (editing.value && !saving.value) cancelEdit();
}

async function handleSave() {
  saving.value = true;
  formError.value = "";

  try {
    const company = await saveAccount(
      props.companyId,
      props.fieldName,
      accountName.value
    );

    accountName.value = company[`${props.fieldName}_name`];

    window.notify.success(`${props.fieldLabel} saved successfully!`);

    editing.value = false;
  } catch (e: any) {
    formError.value = e.message;
  }
  saving.value = false;
}
</script>

<style lang="scss" scoped>
.account-editor {
  .field-label {
    font-weight: 600;
    font-size: 14px;
  }
}
</style>
